<template lang="pug">
div.commentary-image.my-2.mx-n4.my-md-3.mx-md-n6.my-lg-4.mx-lg-n12(
  role="button"
  @click="toggleOpen"
)
  VCard(v-if="isOpen")
    VImg.commentary-image__image.commentary-image__image--full(
      :src="smallUrlAbsolute"
      :srcset="smallUrlAbsolute + ' 420w, ' + largeUrlAbsolute + ' 785w'"
      :lazy-src="placeholderData"
      min-height="100px"
    )
      template(#placeholder)
        VRow.fill-height.ma-0(
          align="center"
          justify="center"
        )
          VProgressCircular(
            :color="dark ? 'primary-c050' : 'primary-c700'"
            :indeterminate="true"
          )
    div.d-flex.flex-column.flex-sm-row
      div
        VCardTitle.text-h6 {{ title }}
        VCardText(v-if="description") {{ description }}
      VCardActions.ml-auto
        VBtn(
          :href="originalUrlAbsolute"
          variant="outlined"
          target="_blank"
          rel="noopener"
          :download="true"
          size="small"
          @click.stop
        )
          VIcon(size="small") {{ mdiOpenInNew }}
          | Original file
  div(v-else)
    VImg.commentary-image__image.commentary-image__image--short(
      :src="placeholderData"
      :eager="true"
      min-height="100px"
      :cover="true"
    )
      template(#placeholder)
        VRow.fill-height.ma-0(
          align="center"
          justify="center"
        )
          VProgressCircular(
            :color="dark ? 'primary-c050' : 'primary-c700'"
            :indeterminate="true"
          )
    div.d-flex.commentary-image__hoverTitle.pa-4.px-md-6.px-lg-12
      div.text-h6 {{ title }}
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { mdiOpenInNew } from "@mdi/js";
import { useDarkMode } from "~/composables/useDarkMode";

const isOpen = ref<boolean>(false);
const dark = useDarkMode();

interface Props {
  title: string;

  description?: string;

  /**
   * URL for the original full size image source. Relative to Drupal base URL.
   */
  originalUrl: string;

  /**
   * URL for the large image source. Relative to Drupal base URL.
   */
  largeUrl: string;

  /**
   * URL for the small image source. Relative to Drupal base URL.
   */
  smallUrl: string;

  /**
   * Base64-encoded data URI of the blurred placeholder image.
   */
  placeholderData: string;
}

const props = withDefaults(defineProps<Props>(), {
  description: "",
});

function toggleOpen() {
  isOpen.value = !isOpen.value;
}

const config = useRuntimeConfig();

const originalUrlAbsolute = computed(() => {
  return new URL(props.originalUrl, config.public.drupal.baseUrl).href;
});

const largeUrlAbsolute = computed(() => {
  return new URL(props.largeUrl, config.public.drupal.baseUrl).href;
});

const smallUrlAbsolute = computed(() => {
  return new URL(props.smallUrl, config.public.drupal.baseUrl).href;
});
</script>

<style lang="scss">
@use "../assets/styles/abstract/functions" as f;

.commentary-image {
  border-radius: f.rem(8);
  overflow: hidden;
  position: relative;
}

.commentary-image__hoverTitle {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  align-items: center;
  padding: f.rem(16);
  background-color: rgba(0, 0, 0, 0.4);
  color: white;
  text-shadow: rgba(28, 49, 58, 0.7) 1px 0 5px;

  .v-theme--light & {
    background-color: rgba(255, 255, 255, 0.7);
    color: black;
    text-shadow: rgba(255, 255, 255, 0.3) 1px 0 5px;
  }
}
</style>
